import { getFileUrl } from '../core/config/api';

/**
 * "Copy Image" / "Save Image" — the photo half of Share to Broadcast.
 *
 * Share to Broadcast can only carry text: the `whatsapp://send?text=` deeplink in
 * Shareme.js has no media parameter, and no URL scheme WhatsApp exposes does. So
 * the stock photo travels separately — the rep copies or saves it, drops it into
 * the Broadcast list, and sends the text with Share to Broadcast.
 *
 * Two mechanisms, picked by capability, both of which deliver the photo:
 *
 *   Copy Image — clipboard.write(). Preferred, because pasting into WhatsApp's
 *   compose box opens a caption field, so the broadcast text can ride along as a
 *   caption and the whole thing goes out as one message.
 *
 *   Save Image — for browsers with no image clipboard. Off iOS that's an
 *   `<a download>`. On iOS it opens the photo in a tab to be long-pressed
 *   instead, because an iOS download lands in the Files app, and WhatsApp
 *   attaches a Files item as a *document* — a file attachment rather than an
 *   inline photo. Long-press -> "Add to Photos" is the only route into the Photo
 *   Library that WhatsApp's photo picker reads.
 *
 * Guessing wrong is never a dead end: clipboard.write() rejects when it fails, so
 * Copy demotes to Save inside the same tap. That is the whole reason this uses
 * the clipboard rather than navigator.share() — a Web Share promise resolves
 * whether or not anything was delivered, so its failures can't be caught.
 */

// All iOS browsers are WebKit, so the branch tracks the OS, not the brand.
const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  // iPadOS 13+ reports a Macintosh UA; touch points separate it from a real Mac.
  (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

const canCopyImage = () =>
  typeof window.ClipboardItem === 'function' &&
  typeof navigator.clipboard?.write === 'function';

/** The card thumbnail's image — the same one the rep is looking at. */
export const shareImageId = (item) => item?.product?.images?.[0]?.id || null;

export const hasShareImage = (item) => !!shareImageId(item);

/** Menu label. Pure function of the environment, so it is safe to call in render. */
export const shareImageLabel = () => (canCopyImage() ? 'Copy Image' : 'Save Image');

// Keep a rejected prefetch from surfacing as an unhandled rejection while still
// leaving the rejection in place for whoever awaits the promise later.
const quiet = (promise) => {
  promise?.catch(() => {});
  return promise;
};

/**
 * Chromium's clipboard accepts image/png only, and these are JPEGs, so the blob
 * goes through a canvas. It stays untainted because /api/files/{id} answers with
 * `Access-Control-Allow-Origin: *` (config/cors.php), which is also why the plain
 * fetch below needs no credentials.
 */
const loadPng = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the photo.'))),
        'image/png',
      );
    };
    img.onerror = () => reject(new Error('Could not load the photo.'));
    img.src = url;
  });

const fetchRaw = (url) =>
  fetch(url).then((response) => {
    if (!response.ok) throw new Error(`Photo request failed (${response.status}).`);
    return response.blob();
  });

const cache = new Map();

const entryFor = (fileId) => {
  if (!cache.has(fileId)) {
    const url = getFileUrl(fileId);
    cache.set(fileId, {
      url,
      // Only fetch what this environment can actually use.
      png: canCopyImage() ? quiet(loadPng(url)) : null,
      raw: isIOS() ? null : quiet(fetchRaw(url)),
    });
  }
  return cache.get(fileId);
};

/**
 * Warm the blob while the menu is opening rather than on the click.
 *
 * This is load-bearing on iOS: Safari treats a tap's user activation as consumed
 * across an await, so fetching the photo inside the click handler and only then
 * calling clipboard.write() throws NotAllowedError. Prefetching keeps the write
 * synchronous with the tap.
 */
export const prefetchShareImage = (item) => {
  const fileId = shareImageId(item);
  if (fileId) entryFor(fileId);
};

const extensionFor = (blob) => (blob.type === 'image/png' ? 'png' : 'jpg');

const saveImage = (entry, fileId, { onInfo, onError }) => {
  if (isIOS()) {
    // Has to be synchronous with the tap or the pop-up blocker eats it.
    const opened = window.open(entry.url, '_blank');
    if (!opened) {
      onError?.('Allow pop-ups for this site, then long-press the photo to save it.');
      return;
    }
    onInfo?.('Long-press the photo and choose "Add to Photos", then attach it in WhatsApp.');
    return;
  }

  // The download attribute is ignored on a cross-origin href, so the anchor has
  // to point at a blob: URL rather than straight at the API.
  entry.raw
    .then((blob) => {
      const href = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = href;
      anchor.download = `broadcast-${fileId}.${extensionFor(blob)}`;
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      // Revoking in the same tick can cancel the download before the browser has
      // read the blob — Firefox in particular.
      setTimeout(() => URL.revokeObjectURL(href), 10000);
      onInfo?.('Photo saved — attach it in WhatsApp, then send the text with Send via WhatsApp.');
    })
    .catch(() => {
      onError?.('Could not save the photo. Open the request and save the image from there.');
    });
};

/**
 * Deliver the photo. Call from a click handler and do not await anything first —
 * see prefetchShareImage above.
 */
export const shareImage = async (item, { onInfo, onError } = {}) => {
  const fileId = shareImageId(item);
  if (!fileId) {
    onError?.('This request has no photo to share.');
    return;
  }

  const entry = entryFor(fileId);

  if (canCopyImage()) {
    try {
      // Hand ClipboardItem the pending promise rather than awaiting it: the spec
      // allows Promise<Blob> for exactly this case, and awaiting first would cost
      // the user activation the write needs on Safari.
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': entry.png })]);
      onInfo?.(
        'Photo copied — paste it into your Broadcast list, then paste the text from Send via WhatsApp as the caption.',
      );
      return;
    } catch {
      // Clipboard unavailable or refused despite advertising support (in-app
      // browsers do this). Fall through — Save always has a working path.
    }
  }

  saveImage(entry, fileId, { onInfo, onError });
};
